import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import { generateId } from "../utils/helpers";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ShortTextIcon from "@mui/icons-material/ShortText";
import SubjectIcon from "@mui/icons-material/Subject";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TitleIcon from "@mui/icons-material/Title";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import RemoveIcon from "@mui/icons-material/Remove";
import TableChartIcon from "@mui/icons-material/TableChart";
import BuildIcon from "@mui/icons-material/Build";
import WidgetsIcon from "@mui/icons-material/Widgets";
import ImageIcon from "@mui/icons-material/Image";
import ComponentTools from "./ComponentTools";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";

// Función para generar números únicos de 6 dígitos con ceros a la izquierda
const generateUniqueNumber = () => {
  // Generar un número aleatorio entre 1 y 999999
  const randomNumber = Math.floor(Math.random() * 999999) + 1;
  // Convertir a string y rellenar con ceros a la izquierda hasta 6 dígitos
  const paddedNumber = randomNumber.toString().padStart(6, "0");
  // Concatenar con el prefijo "NRO #"
  return `NRO ${paddedNumber}`;
};

const Sidebar = ({ isOpen, selectedComponent, onComponentSelect, onComponentUpdate }) => {
  const [componentsOpen, setComponentsOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(300);

  const componentTypes = [
    {
      type: "formHeader",
      label: "Cabecera",
      icon: <TitleIcon />,
      description: "Cabecera fija del formulario",
      color: "primary",
    },
    {
      type: "header",
      label: "Título",
      icon: <TitleIcon />,
      description: "Título principal del formulario",
      color: "primary",
    },
    {
      type: "section",
      label: "Sección",
      icon: <ViewHeadlineIcon />,
      description: "Agrupa campos relacionados",
      color: "secondary",
    },
    {
      type: "label",
      label: "Etiqueta",
      icon: <TextFieldsIcon />,
      description: "Texto descriptivo",
      color: "info",
    },
    {
      type: "input",
      label: "Texto",
      icon: <ShortTextIcon />,
      description: "Entrada de texto corto",
      color: "success",
    },
    {
      type: "textArea",
      label: "Área",
      icon: <SubjectIcon />,
      description: "Entrada de texto largo",
      color: "warning",
    },
    {
      type: "select",
      label: "Selector",
      icon: <ExpandMoreIcon />,
      description: "Lista desplegable",
      color: "error",
    },
    {
      type: "checklist",
      label: "Lista",
      icon: <CheckBoxIcon />,
      description: "Múltiples opciones",
      color: "primary",
    },
    {
      type: "radiobutton",
      label: "Radio",
      icon: <RadioButtonCheckedIcon />,
      description: "Una opción de varias",
      color: "secondary",
    },
    {
      type: "image",
      label: "Imagen",
      icon: <ImageIcon />,
      description: "Cargar imagen desde archivo",
      color: "secondary",
    },
    {
      type: "divider",
      label: "Separador",
      icon: <RemoveIcon />,
      description: "Línea divisoria",
      color: "info",
    },
    {
      type: "cells",
      label: "Celdas",
      icon: <TableChartIcon />,
      description: "Tabla editable",
      color: "warning",
    },
  ];

  const handleDragStart = (e, componentType) => {
    const component = createComponent(componentType);
    e.dataTransfer.setData("application/json", JSON.stringify(component));
    e.dataTransfer.effectAllowed = "copy";
  };

  const createComponent = (type) => {
    // Calcular tamaños óptimos según el tipo de componente
    const getOptimalSize = (componentType) => {
      switch (componentType) {
        case "header":
          return { width: 80, height: 15 };
        case "section":
          return { width: 70, height: 12 };
        case "label":
          return { width: 40, height: 8 };
        case "input":
          return { width: 50, height: 10 };
        case "textArea":
          return { width: 60, height: 15 };
        case "select":
          return { width: 45, height: 10 };
        case "checklist":
          return { width: 55, height: 20 };
        case "radiobutton":
          return { width: 55, height: 20 };
        case "button":
          return { width: 30, height: 8 };
        case "image":
          return { width: 50, height: 20 };
        case "divider":
          return { width: 80, height: 2 };
        case "formHeader":
          return { width: 100, height: 8 };
        case "cells":
          return { width: 70, height: 25 };
        default:
          return { width: 40, height: 10 };
      }
    };

    const optimalSize = getOptimalSize(type);

    const baseComponent = {
      id: generateId(),
      type: type,
      rndPosition: {
        x: 0,
        y: 0,
        width: optimalSize.width,
        height: optimalSize.height,
      },
      style: {
        backgroundColor: "#ffffff",
        borderRadius: 4,
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: { top: 8, right: 8, bottom: 8, left: 8 },
        margin: { top: 0, right: 0, bottom: 0, left: 0 },
        textStyle: {
          fontSize: 14,
          fontWeight: "normal",
          color: "#000000",
          textAlign: "left",
          fontFamily: "inherit",
        },
        shadow: {
          enabled: false,
          color: "rgba(0,0,0,0.2)",
          blur: 4,
          offsetX: 0,
          offsetY: 2,
        },
      },
    };
    switch (type) {
      case "header":
        return {
          ...baseComponent,
          title: "Título del formulario",
          subtitle: "Subtítulo opcional",
        };
      case "section":
        return {
          ...baseComponent,
          title: "Nueva sección",
          description: "Descripción de la sección",
        };
      case "label":
        return {
          ...baseComponent,
          text: "Etiqueta",
        };
      case "input":
        return {
          ...baseComponent,
          placeholder: "Escribe aquí...",
          value: "",
          label: "Campo de texto",
          required: false,
        };
      case "textArea":
        return {
          ...baseComponent,
          placeholder: "Escribe aquí...",
          value: "",
          label: "Área de texto",
          required: false,
          rows: 3,
        };
      case "select":
        return {
          ...baseComponent,
          label: "Selector",
          options: ["Opción 1", "Opción 2", "Opción 3"],
          selectedOption: "",
          required: false,
        };
      case "checklist":
        return {
          ...baseComponent,
          label: "Lista de verificación",
          options: ["Opción 1", "Opción 2", "Opción 3"],
          selectedOptions: [],
          required: false,
        };
      case "radiobutton":
        return {
          ...baseComponent,
          label: "Botones de radio",
          options: ["Opción 1", "Opción 2", "Opción 3"],
          selectedOption: "",
          required: false,
        };
      case "button":
        return {
          ...baseComponent,
          label: "Enviar",
          action: "submit",
        };
      case "image":
        return {
          ...baseComponent,
          src: null,
          alt: "Imagen del formulario",
          placeholder: "Haz clic para cargar imagen",
          objectFit: "contain",
        };
      case "divider":
        return {
          ...baseComponent,
        };
      case "formHeader":
        return {
          ...baseComponent,
          logo: "https://te-ayudo-img.s3.us-east-2.amazonaws.com/production/users/5f2d8809bceaa5798cbf861c@1663253858475",
          nameLogo: "Nombre del Hospital",
          titleName: "Nuevo Formulario",
          number: generateUniqueNumber(), // Generar número único automáticamente
        };
      case "cells":
        return {
          ...baseComponent,
          rows: 3,
          columns: 3,
          showHeaders: true,
          headerRow: ["Columna 1", "Columna 2", "Columna 3"],
          cellData: {},
        };
      default:
        return baseComponent;
    }
  };

  const handleComponentClick = (componentType) => {
    const component = createComponent(componentType);
    onComponentSelect(component);
  };

  if (!isOpen) return null;

  return (
    <ResizableBox
      width={sidebarWidth}
      height={window.innerHeight}
      axis="x"
      minConstraints={[140, window.innerHeight]}
      maxConstraints={[320, window.innerHeight]}
      resizeHandles={["w"]}
      onResizeStop={(e, { size }) => setSidebarWidth(size.width)}
      handle={
        <span
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: "ew-resize",
            zIndex: 1000,
            background: "transparent",
          }}
        />
      }
      style={{ position: "relative", zIndex: 100, height: "100vh", maxHeight: "100vh" }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: "100vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Sección de Componentes */}
        <Accordion
          expanded={componentsOpen}
          onChange={(e, expanded) => setComponentsOpen(expanded)}
          sx={{
            boxShadow: "none",
            "&:before": { display: "none" },
            "& .MuiAccordionSummary-root": {
              minHeight: "48px",
              "&.Mui-expanded": { minHeight: "48px" },
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: "action.hover",
              "&:hover": { bgcolor: "action.selected" },
              borderRadius: 1,
              mx: 1,
              mt: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WidgetsIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" fontWeight="medium">
                Componentes
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2, pb: 1 }}>
            <Grid container spacing={1}>
              {componentTypes.map((component) => (
                <Grid item xs={4} key={component.type}>
                  <Tooltip title={component.description} placement="top" arrow>
                    <Button
                      variant="outlined"
                      size="small"
                      draggable
                      onDragStart={(e) => handleDragStart(e, component.type)}
                      onClick={() => handleComponentClick(component.type)}
                      sx={{
                        width: "100%",
                        height: "60px",
                        flexDirection: "column",
                        gap: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: "medium",
                        borderColor: `${component.color}.main`,
                        color: `${component.color}.main`,
                        "&:hover": {
                          bgcolor: `${component.color}.50`,
                          borderColor: `${component.color}.dark`,
                          transform: "scale(1.05)",
                          transition: "transform 0.2s",
                        },
                      }}
                    >
                      <Box sx={{ color: `${component.color}.main` }}>{component.icon}</Box>
                      {component.label}
                    </Button>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Herramientas */}
        {selectedComponent && (
          <Accordion
            expanded={toolsOpen}
            onChange={(e, expanded) => setToolsOpen(expanded)}
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
              "& .MuiAccordionSummary-root": {
                minHeight: "48px",
                "&.Mui-expanded": { minHeight: "48px" },
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: "action.hover",
                "&:hover": { bgcolor: "action.selected" },
                borderRadius: 1,
                mx: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BuildIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight="medium">
                  Herramientas
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* <Box sx={{ mb: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                  Editando: <span style={{ textTransform: 'capitalize', color: 'primary.main', fontWeight: 'medium' }}>
                    {selectedComponent.type}
                  </span>
                </Typography>
              </Box> */}
              <ComponentTools
                component={selectedComponent}
                onUpdate={(updates) => onComponentUpdate(selectedComponent.id, updates)}
              />
              <div style={{ height: "100px" }}></div>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
    </ResizableBox>
  );
};

export default Sidebar;
